import { useEffect } from 'react';
import { notify } from '@/feature/notifications';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore';
import { evaluateLiquidationAlerts, liquidationAlertPositionKey } from '../../features/account/evaluateLiquidationAlerts';
import { useLiquidationAlertsStore } from '../../features/account/liquidationAlertsStore';
import useAppStore from '../rootStore';

/**
 * Format a liquidation price for the alert message: comma-grouped, at the market's quote
 * precision, with no forced trailing zeros — matching how prices render in the position table
 * (see `buildPriceColumn` / `NumberField`, which uses `quoteDecimals` as max fraction digits).
 */
function formatLiquidationPrice(price: number, quoteDecimals: number): string {
  return price.toLocaleString('en-US', { maximumFractionDigits: Math.max(0, quoteDecimals) });
}

export function useLiquidationAlertsWatcher(): void {
  const lastRecalculatedAt = usePositionStore((s) => s.lastRecalculatedAt);

  useEffect(() => {
    if (lastRecalculatedAt == null) return;
    const positions = usePositionStore.getState().positions;
    const markets = useAppStore.getState().markets;
    if (positions.length === 0 || !markets || markets.size === 0) return;

    const alerts = useLiquidationAlertsStore.getState();
    const now = Date.now();

    const decisions = evaluateLiquidationAlerts({
      positions,
      markets,
      prefs: {
        enabled: alerts.enabled,
        marginUsedPercent: alerts.marginUsedPercent,
        cooldownMinutes: alerts.cooldownMinutes,
      },
      lastNotifiedAt: alerts.lastNotifiedAt,
      now,
    });

    for (const decision of decisions) {
      const liqPrice = formatLiquidationPrice(decision.position.liquidationPrice, decision.position.quoteDecimals);
      notify(
        `Liquidation risk on ${decision.marketName}. ${decision.marginUsedPercent.toFixed(0)}% of the way to liquidation. Liq. price ${liqPrice} — about a ${decision.bufferPercent.toFixed(2)}% move away.`,
        'warning',
        {
          replaceKey: `liquidation-alert-${decision.positionKey}`,
          action: { kind: 'switchToMarket', label: 'View Market', marketId: decision.position.marketId },
          secondaryAction: { kind: 'openSettings', label: 'Alert Settings' },
          criticalOverride: true,
          manualDismiss: true,
        }
      );
      alerts.markNotified(decision.positionKey, now);
    }

    alerts.retainPositions(new Set(positions.map(liquidationAlertPositionKey)));
  }, [lastRecalculatedAt]);
}
